import type { SimpleProp } from "building-types/props/propTypes";
import { GROUND_LEVEL } from "common/constants/buildingObjects";
import {
  MS_Grass_Lawn_01__Default,
  MS_Tree_Foliage_Generic__Default,
} from "common/materials/library/designMaterialSrcDefaults";
import type { MaterialSrc } from "common/materials/materialSrc";

export const sitePropDefaultValues: SimpleProp = {
  type: "SIMPLE",
  propName: "Lot_GenericTestTree_Center",
  x: 0,
  y: 0,
  z: GROUND_LEVEL,
  // material: MS_Grass_Lawn_01__Default,
  castShadow: false,
  // scaleUV: true,
  // scaleUVMult: 1.0,
  scale: 1,
};

export interface SiteProp {
  type: "SITE";
  url: string;
  material: MaterialSrc;
  x?: number;
  y?: number;
  z?: number;
  // The rotations are applied as Euler angles in the usual three.js fashion: first x rotation, then y, then z.
  // They're given in degrees.
  rotX?: number;
  rotY?: number;
  rotZ?: number;

  scale?: number;
  castShadow?: boolean;

  // For props that want to use designmaterial sdf, we need to scale the uvs
  // up to world space values, these two fields let you do that.
  scaleUV?: boolean;
  scaleUVMult?: number;
}

/**
 * A variant of SiteProp with more required fields.
 */
export interface SitePropVariant1 extends SiteProp {
  x: number;
  y: number;
  z: number;

  scale: number;
  castShadow: boolean;

  scaleUV: boolean;
  scaleUVMult: number;
}

/**
 * A variant of SiteProp with even more required fields.
 */
export interface SitePropVariant2 extends SitePropVariant1 {
  rotX: number;
  rotY: number;
  rotZ: number;
}

export const propSiteScale = 1.25;

export const defaultGroundProp: SitePropVariant1 = {
  type: "SITE",
  url: "/static/propsTest/GenericLot/DV_Lot_GenericDisc_03.drc",
  x: 0,
  y: 0,
  z: GROUND_LEVEL,
  material: MS_Grass_Lawn_01__Default,
  castShadow: false,
  scaleUV: true,
  scaleUVMult: 1.0,
  scale: propSiteScale,
};

export const defaultSiteProps: (SiteProp | SimpleProp)[] = [
  // {
  //   type: "SIMPLE",
  //   propName: "WoodlawnProp_APP_01",
  //   x: 0,
  //   y: 0,
  //   z: 0,

  //   rotZ: 90,
  //   scale: 1,
  // },

  /////////
  // BUSHES
  /////////

  {
    //Bush 01
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 0,
    y: 1628,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 1.96,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 02
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -340,
    y: 1691,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 9,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 03
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -597,
    y: 1666,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 100,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 04
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -810,
    y: 1455,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 27,

    scale: 1.35,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 05
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -1062,
    y: 1461,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -34,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 06
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -1193,
    y: 1219,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 45,

    scale: 1.6,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 07
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -1312,
    y: 943,
    z: -50,

    rotX: 0,
    rotY: 0,
    rotZ: 54,

    scale: 1.3,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 08
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -1609,
    y: 820,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 63,

    scale: 1.6,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 09
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -1718,
    y: 558,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 225,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 10
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -1741,
    y: 283,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 81,

    scale: 1.48,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 11
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -1806,
    y: -31,
    z: -42,

    rotX: 0,
    rotY: 0,
    rotZ: 90,

    scale: 1.12,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 12
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -1784,
    y: -283,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 99,

    scale: 1.7,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 13
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -1631,
    y: -558,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 11,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 14
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -1609,
    y: -820,
    z: -47,

    rotX: 0,
    rotY: 0,
    rotZ: 117,

    scale: 1.2,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 15
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -1461,
    y: -1062,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 71,

    scale: 1.6,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 16
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -1277,
    y: -1277,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 82,

    scale: 1.35,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 17
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -1062,
    y: -1461,
    z: -55,

    rotX: 0,
    rotY: 0,
    rotZ: 226,

    scale: 1.48,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 18
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -783,
    y: -1544,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 153,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 19
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -588,
    y: -1718,
    z: -42,

    rotX: 0,
    rotY: 0,
    rotZ: 162,

    scale: 1.1,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 20
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -259,
    y: -1784,
    z: -26,

    rotX: 0,
    rotY: 0,
    rotZ: 48,

    scale: 1.42,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 21
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -4.613,
    y: -1729,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -63,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 22
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 283,
    y: -1784,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -171,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 23
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 602,
    y: -1612,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -162,

    scale: 1.7,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 24
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 893,
    y: -1609,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -32,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 25
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 1021,
    y: -1369,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -69,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 26
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 1277,
    y: -1277,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -116,

    scale: 1.65,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 27
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 1461,
    y: -1001,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -349,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 28
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 1543,
    y: -783,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -117,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 29
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 1718,
    y: -558,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -108,

    scale: 1.8,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 30
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 1632,
    y: -235,
    z: -50,

    rotX: 0,
    rotY: 0,
    rotZ: -99,

    scale: 1.2,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 31
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 1806,
    y: 0,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -159,

    scale: 2,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 32
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 1784,
    y: 337,
    z: -45,

    rotX: 0,
    rotY: 0,
    rotZ: -81,

    scale: 1.2,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 33
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 1718,
    y: 612,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -72,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 34
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 1609,
    y: 898,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -63,

    scale: 1.3,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 35
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 1365,
    y: 1062,
    z: -39,

    rotX: 0,
    rotY: 0,
    rotZ: -54,

    scale: 1.2,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 36
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 1277,
    y: 1324,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -45,

    scale: 1.14,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 37
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 956,
    y: 1461,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -100,

    scale: 1.7,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 38
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 722,
    y: 1647,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -27,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 39
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 558,
    y: 1718,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 103,

    scale: 1.5,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 40
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 282.5,
    y: 1784,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -73,

    scale: 1.37,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 41
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: -165,
    y: 1806,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 1.2,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },
  {
    //Bush 42
    type: "SITE",
    url: "/static/propsTest/GenericLot/DV_Lot_TestBush_Single_01.drc",
    x: 1546,
    y: -829,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 1.2,

    material: MS_Tree_Foliage_Generic__Default,
    castShadow: true,
  },

  /////////
  // TREES
  /////////

  {
    //Tree 01
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -905,
    y: -742,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -74,

    scale: 0.545,
  },
  {
    //Tree 02
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -878,
    y: -853,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 121,

    scale: 0.444,
  },
  {
    //Tree 03
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -348,
    y: -1127,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 0.773,
  },
  {
    //Tree 04
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 26,
    y: -1235,
    z: -65,

    rotX: 0,
    rotY: 0,
    rotZ: -69,

    scale: 1.375,
  },
  {
    //Tree 05
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 671,
    y: -1054,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 0.685,
  },
  {
    //Tree 06
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 944,
    y: -1171,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -125,

    scale: 1.454,
  },
  {
    //Tree 07
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 1571,
    y: 174,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -101,

    scale: 0.881,
  },
  {
    //Tree 08
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 1238,
    y: -597,
    z: -12,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 1.689,
  },
  {
    //Tree 09
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 1424,
    y: 370,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 125,

    scale: 1.291,
  },
  {
    //Tree 10
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 589,
    y: 1171,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 0.733,
  },
  {
    //Tree 11
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 169,
    y: 1220,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 109,

    scale: 0.857,
  },
  {
    //Tree 12
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 415,
    y: 1339,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 1.066,
  },
  {
    //Tree 13
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -582,
    y: 1112,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 99,

    scale: 1.145,
  },
  {
    //Tree 14
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -257,
    y: 1651,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 0.982,
  },
  {
    //Tree 15
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -447,
    y: 1498,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 88,

    scale: 1.221,
  },
  {
    //Tree 16
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -646,
    y: 1536,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -159,

    scale: 0.837,
  },
  {
    //Tree 17
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -1034,
    y: 956,
    z: -54,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 1.299,
  },
  {
    //Tree 18
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -1418,
    y: 700,
    z: -90,

    rotX: 0,
    rotY: 0,
    rotZ: 126,

    scale: 1.936,
  },
  {
    //Tree 19
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -1252,
    y: 340,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 1.334,
  },
  {
    //Tree 20
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -1585,
    y: 125,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -128,

    scale: 1.499,
  },
  {
    //Tree 21
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -1427,
    y: -246,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 1.458,
  },
  {
    //Tree 22
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -1158,
    y: -473,
    z: -84,

    rotX: 0,
    rotY: 0,
    rotZ: 50,

    scale: 1.382,
  },
  {
    //Tree 23
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -1242,
    y: -879,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 0.751,
  },
  {
    //Tree 24
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -1129,
    y: -1169,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -195,

    scale: 1.155,
  },
  {
    //Tree 25
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -535,
    y: -1558,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 1.132,
  },
  {
    //Tree 26
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -878,
    y: -1255,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 86,

    scale: 0.711,
  },
  {
    //Tree 27
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: -212,
    y: -1450,
    z: -68,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 1.314,
  },
  {
    //Tree 28
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 318,
    y: -1471,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 104,

    scale: 1.318,
  },
  {
    //Tree 29
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 514,
    y: -1268,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 0.792,
  },
  {
    //Tree 30
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 1224,
    y: -985,
    z: -59,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 1.057,
  },
  {
    //Tree 31
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 1565,
    y: -467,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -87,

    scale: 1.19,
  },
  {
    //Tree 32
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 1379,
    y: 10,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 77,

    scale: 1.102,
  },
  {
    //Tree 33
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 1495,
    y: 777,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 0.726,
  },
  {
    //Tree 34
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 1253,
    y: 947,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 44,

    scale: 1.633,
  },
  {
    //Tree 35
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 988,
    y: 988,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: -53,

    scale: 0.835,
  },
  {
    //Tree 36
    type: "SIMPLE",
    propName: "Lot_GenericTestTree_Center",

    x: 1067,
    y: 1226,
    z: GROUND_LEVEL,

    rotX: 0,
    rotY: 0,
    rotZ: 0,

    scale: 1.087,
  },
];
